import React, { useEffect, useRef, Component } from 'react';
import { AvatarState } from './AvatarControls';
import { AvatarStyleType } from './StyleSwitcher';
// --- Smooth Noise ---
const PERLIN_SIZE = 4095;
const PERLIN_YWRAPB = 4;
const PERLIN_YWRAP = 1 << PERLIN_YWRAPB;
const PERLIN_ZWRAPB = 8;
const PERLIN_ZWRAP = 1 << PERLIN_ZWRAPB;
const sc = (i: number) => 0.5 * (1 - Math.cos(i * Math.PI));
let _p: number[] | null = null;
function noise(x: number, y = 0, z = 0): number {
  if (!_p) _p = Array.from({
    length: PERLIN_SIZE + 1
  }, () => Math.random());
  if (x < 0) x = -x;
  if (y < 0) y = -y;
  if (z < 0) z = -z;
  let xi = ~~x,
    yi = ~~y,
    zi = ~~z;
  let xf = x - xi,
    yf = y - yi,
    zf = z - zi;
  let r = 0,
    a = 0.5;
  for (let o = 0; o < 4; o++) {
    let of_ = xi + (yi << PERLIN_YWRAPB) + (zi << PERLIN_ZWRAPB);
    const rx = sc(xf),
      ry = sc(yf);
    let n1 = _p[of_ & PERLIN_SIZE];
    n1 += rx * (_p[of_ + 1 & PERLIN_SIZE] - n1);
    let n2 = _p[of_ + PERLIN_YWRAP & PERLIN_SIZE];
    n2 += rx * (_p[of_ + PERLIN_YWRAP + 1 & PERLIN_SIZE] - n2);
    n1 += ry * (n2 - n1);
    of_ += PERLIN_ZWRAP;
    n2 = _p[of_ & PERLIN_SIZE];
    n2 += rx * (_p[of_ + 1 & PERLIN_SIZE] - n2);
    let n3 = _p[of_ + PERLIN_YWRAP & PERLIN_SIZE];
    n3 += rx * (_p[of_ + PERLIN_YWRAP + 1 & PERLIN_SIZE] - n3);
    n2 += ry * (n3 - n2);
    r += (n1 + zf * (n2 - n1)) * a;
    a *= 0.5;
    xi <<= 1;
    xf *= 2;
    yi <<= 1;
    yf *= 2;
    zi <<= 1;
    zf *= 2;
    if (xf >= 1) {
      xi++;
      xf--;
    }
    if (yf >= 1) {
      yi++;
      yf--;
    }
    if (zf >= 1) {
      zi++;
      zf--;
    }
  }
  return r;
}
function snoise(x: number, y = 0, z = 0): number {
  return (noise(x, y, z) - 0.5) * 2;
}
// --- State Configuration ---
interface StateConfig {
  speed: number;
  scale: number;
  breathe: number;
  breatheSpeed: number;
  shiftX: number;
  orangeGlow: number;
  coreFocus: number;
  drift: number;
  luminance: number;
  innerSpeed: number;
}
const STATES: Record<AvatarState, StateConfig> = {
  idle: {
    speed: 1.0,
    scale: 1.0,
    breathe: 0.012,
    breatheSpeed: 0.0008,
    shiftX: 0.08,
    orangeGlow: 0.35,
    coreFocus: 0.0,
    drift: 1.0,
    luminance: 1.0,
    innerSpeed: 1.0
  },
  listening: {
    speed: 0.6,
    scale: 0.88,
    breathe: 0.005,
    breatheSpeed: 0.0004,
    shiftX: 0.18,
    orangeGlow: 0.15,
    coreFocus: 0.45,
    drift: 0.4,
    luminance: 0.75,
    innerSpeed: 0.5
  },
  speaking: {
    speed: 1.4,
    scale: 1.04,
    breathe: 0.035,
    breatheSpeed: 0.003,
    shiftX: 0.05,
    orangeGlow: 0.55,
    coreFocus: 0.15,
    drift: 1.6,
    luminance: 1.3,
    innerSpeed: 2.0
  },
  emphasis: {
    speed: 0.9,
    scale: 0.94,
    breathe: 0.008,
    breatheSpeed: 0.005,
    shiftX: 0.1,
    orangeGlow: 0.9,
    coreFocus: 0.75,
    drift: 0.8,
    luminance: 1.5,
    innerSpeed: 1.2
  }
};
function lerpConfig(a: StateConfig, b: StateConfig, t: number): StateConfig {
  const l = (v1: number, v2: number) => v1 + (v2 - v1) * t;
  return {
    speed: l(a.speed, b.speed),
    scale: l(a.scale, b.scale),
    breathe: l(a.breathe, b.breathe),
    breatheSpeed: l(a.breatheSpeed, b.breatheSpeed),
    shiftX: l(a.shiftX, b.shiftX),
    orangeGlow: l(a.orangeGlow, b.orangeGlow),
    coreFocus: l(a.coreFocus, b.coreFocus),
    drift: l(a.drift, b.drift),
    luminance: l(a.luminance, b.luminance),
    innerSpeed: l(a.innerSpeed, b.innerSpeed)
  };
}
// --- Drawing Functions ---
function drawOrb(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number, baseDriftX: number, baseDriftY: number, asymX: number) {
  const lum = s.luminance;
  // Layer 1: Atmosphere
  ctx.save();
  ctx.translate(cx + asymX * 0.5 + baseDriftX * 0.8, cy + baseDriftY * 0.8);
  ctx.scale(totalScale, totalScale);
  const g1 = ctx.createRadialGradient(0, 0, baseR * 0.1, 0, 0, baseR * 1.7);
  g1.addColorStop(0, 'rgba(0, 51, 102, ' + Math.min(1, 0.85 * lum) + ')');
  g1.addColorStop(0.35, 'rgba(0, 38, 77, ' + Math.min(1, 0.65 * lum) + ')');
  g1.addColorStop(0.65, 'rgba(0, 26, 51, ' + 0.3 * lum + ')');
  g1.addColorStop(0.85, 'rgba(0, 20, 40, ' + 0.1 * lum + ')');
  g1.addColorStop(1, 'rgba(0, 15, 30, 0)');
  ctx.fillStyle = g1;
  ctx.beginPath();
  ctx.arc(0, 0, baseR * 1.7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Layer 2: Main Body
  ctx.save();
  ctx.translate(cx + asymX + baseDriftX, cy + baseDriftY);
  ctx.scale(totalScale, totalScale);
  const bodyR = baseR * (1.15 - s.coreFocus * 0.15);
  const g2 = ctx.createRadialGradient(0, 0, 0, 0, 0, bodyR);
  g2.addColorStop(0, 'rgba(0, 89, 179, ' + Math.min(1, 0.9 * lum) + ')');
  g2.addColorStop(0.3, 'rgba(0, 70, 140, ' + Math.min(1, 0.7 * lum) + ')');
  g2.addColorStop(0.6, 'rgba(0, 51, 102, ' + 0.4 * lum + ')');
  g2.addColorStop(0.85, 'rgba(0, 30, 60, ' + 0.12 * lum + ')');
  g2.addColorStop(1, 'rgba(0, 20, 40, 0)');
  ctx.fillStyle = g2;
  ctx.beginPath();
  ctx.arc(0, 0, bodyR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Layer 3: Core
  const innerT = t * s.innerSpeed;
  const l3x = baseDriftX * 1.2 + snoise(innerT * 0.25, 20) * 6 * s.drift;
  const l3y = baseDriftY * 1.2 + snoise(innerT * 0.25, 25) * 5 * s.drift;
  const coreR = baseR * (0.55 - s.coreFocus * 0.2);
  ctx.save();
  ctx.translate(cx + asymX * 1.3 + l3x, cy + l3y);
  ctx.scale(totalScale, totalScale);
  const coreLum = Math.min(1, lum * (1 + s.coreFocus * 0.4));
  const g3 = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
  g3.addColorStop(0, 'rgba(51, 153, 255, ' + Math.min(1, 0.85 * coreLum) + ')');
  g3.addColorStop(0.35, 'rgba(0, 115, 230, ' + Math.min(1, 0.55 * coreLum) + ')');
  g3.addColorStop(0.7, 'rgba(0, 70, 140, ' + 0.2 * coreLum + ')');
  g3.addColorStop(1, 'rgba(0, 40, 80, 0)');
  ctx.fillStyle = g3;
  ctx.beginPath();
  ctx.arc(0, 0, coreR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Layer 4: Internal Currents
  for (let i = 0; i < 2; i++) {
    const angle = innerT * 0.08 + i * Math.PI * 0.7;
    const cDist = baseR * 0.15 * s.drift;
    const cOffX = Math.cos(angle) * cDist;
    const cOffY = Math.sin(angle) * cDist * 0.6;
    const cR = baseR * (0.4 + snoise(innerT * 0.12 + i * 50, 30) * 0.08);
    ctx.save();
    ctx.translate(cx + asymX + baseDriftX + cOffX, cy + baseDriftY + cOffY);
    ctx.scale(totalScale, totalScale);
    ctx.rotate(angle * 0.5);
    ctx.scale(1.3, 0.7);
    const gc = ctx.createRadialGradient(0, 0, 0, 0, 0, cR);
    gc.addColorStop(0, 'rgba(26, 128, 230, ' + 0.18 * lum * s.drift + ')');
    gc.addColorStop(0.5, 'rgba(0, 89, 179, ' + 0.08 * lum * s.drift + ')');
    gc.addColorStop(1, 'rgba(0, 50, 100, 0)');
    ctx.fillStyle = gc;
    ctx.beginPath();
    ctx.arc(0, 0, cR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  // Layer 5: Orange Accent
  const oAngle = t * 0.06 + 0.3;
  const oDist = baseR * 0.3;
  const oBaseX = cx + asymX * 2 + Math.cos(oAngle) * oDist * 0.3;
  const oBaseY = cy - baseR * 0.1 + Math.sin(oAngle) * oDist * 0.2;
  const oR = baseR * 0.18 * (0.6 + s.orangeGlow * 0.4);
  const oA = Math.min(1, s.orangeGlow * lum * 0.8);
  if (oA > 0.02) {
    ctx.save();
    ctx.translate(oBaseX + baseDriftX, oBaseY + baseDriftY);
    ctx.scale(totalScale, totalScale);
    ctx.globalCompositeOperation = 'screen';
    const go = ctx.createRadialGradient(0, 0, 0, 0, 0, oR);
    go.addColorStop(0, 'rgba(232, 119, 34, ' + oA + ')');
    go.addColorStop(0.4, 'rgba(245, 145, 61, ' + oA * 0.45 + ')');
    go.addColorStop(1, 'rgba(180, 80, 10, 0)');
    ctx.fillStyle = go;
    ctx.beginPath();
    ctx.arc(0, 0, oR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
function drawAura(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const lum = s.luminance;
  const auraScale = totalScale * 1.2;
  const auraT = t * 0.5;
  ctx.globalCompositeOperation = 'screen';
  // Deep blue base
  const b1x = Math.sin(auraT * 0.3) * 20 * s.drift;
  const b1y = Math.cos(auraT * 0.25) * 15 * s.drift;
  ctx.save();
  ctx.translate(cx + b1x - baseR * 0.1, cy + b1y);
  ctx.scale(auraScale, auraScale * 0.9);
  const gB = ctx.createRadialGradient(0, 0, 0, 0, 0, baseR * 1.6);
  gB.addColorStop(0, 'rgba(30, 80, 200, ' + 0.5 * lum + ')');
  gB.addColorStop(0.5, 'rgba(20, 60, 160, ' + 0.25 * lum + ')');
  gB.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gB;
  ctx.beginPath();
  ctx.arc(0, 0, baseR * 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Purple/violet cloud
  const p1x = Math.sin(auraT * 0.4 + 2) * 30 * s.drift;
  const p1y = Math.cos(auraT * 0.35 + 1) * 25 * s.drift;
  ctx.save();
  ctx.translate(cx + p1x + baseR * 0.15, cy + p1y - baseR * 0.05);
  ctx.scale(auraScale * 0.95, auraScale);
  const gP = ctx.createRadialGradient(0, 0, 0, 0, 0, baseR * 1.3);
  gP.addColorStop(0, 'rgba(160, 80, 220, ' + 0.4 * lum + ')');
  gP.addColorStop(0.5, 'rgba(120, 60, 180, ' + 0.15 * lum + ')');
  gP.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gP;
  ctx.beginPath();
  ctx.arc(0, 0, baseR * 1.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Teal accent
  const t1x = Math.sin(auraT * 0.5 + 4) * 25 * s.drift;
  const t1y = Math.cos(auraT * 0.45 + 3) * 20 * s.drift;
  ctx.save();
  ctx.translate(cx + t1x, cy + t1y + baseR * 0.1);
  ctx.scale(auraScale * 0.8, auraScale * 0.85);
  const gT = ctx.createRadialGradient(0, 0, 0, 0, 0, baseR * 1.0);
  gT.addColorStop(0, 'rgba(0, 180, 200, ' + 0.3 * lum + ')');
  gT.addColorStop(0.6, 'rgba(0, 120, 150, ' + 0.1 * lum + ')');
  gT.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gT;
  ctx.beginPath();
  ctx.arc(0, 0, baseR * 1.0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Bright white/cyan core
  const coreScale = auraScale * (0.7 + s.coreFocus * 0.3);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(coreScale, coreScale);
  const gC = ctx.createRadialGradient(0, 0, 0, 0, 0, baseR * 0.6);
  gC.addColorStop(0, 'rgba(200, 230, 255, ' + 0.6 * lum + ')');
  gC.addColorStop(0.4, 'rgba(150, 200, 255, ' + 0.25 * lum + ')');
  gC.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gC;
  ctx.beginPath();
  ctx.arc(0, 0, baseR * 0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
function drawBlobs(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const lum = s.luminance;
  const blobCount = 4;
  // Raw elapsed time — smooth, decoupled from state speed
  const elapsed = performance.now() / 1000;
  // Gentle constant-speed time for positions
  const blobT = elapsed * 0.25; // Moderate speed — visible but smooth
  // Dampen scale slightly
  const dampedScale = 1.0 + (totalScale - 1.0) * 0.35;
  // Blob sizes — middle ones larger, outer ones smaller (like the screenshot)
  const sizes = [0.28, 0.36, 0.34, 0.3];
  // Base spacing — close together so they overlap
  const spacing = baseR * 0.42;
  for (let i = 0; i < blobCount; i++) {
    // Horizontal arrangement centered
    const baseX = (i - (blobCount - 1) / 2) * spacing;
    // Moderate organic drift — visible movement but smooth
    const driftX = snoise(blobT * 0.7 + i * 4.3, 0.5) * 18;
    const driftY = snoise(blobT * 0.6 + i * 4.3, 3.8) * 14;
    // Individual gentle breathing
    const breath = Math.sin(elapsed * (0.5 + i * 0.12) + i * 1.7) * 0.02;
    const blobScale = dampedScale + breath;
    const blobR = baseR * sizes[i];
    // Slight squash/stretch — some blobs are oval like the screenshot
    const squash = 1.0 + snoise(blobT * 0.4 + i * 9, 12) * 0.08;
    ctx.save();
    ctx.translate(cx + baseX + driftX, cy + driftY);
    ctx.scale(blobScale * (1.0 + snoise(blobT * 0.3 + i * 6, 8) * 0.04), blobScale * squash);
    // Much more solid — high opacity center, sharper falloff
    const gB = ctx.createRadialGradient(0, 0, 0, 0, 0, blobR);
    gB.addColorStop(0, 'rgba(255, 255, 255, ' + Math.min(1, 0.98 * lum) + ')');
    gB.addColorStop(0.65, 'rgba(255, 255, 255, ' + Math.min(1, 0.92 * lum) + ')');
    gB.addColorStop(0.85, 'rgba(240, 245, 255, ' + Math.min(1, 0.5 * lum) + ')');
    gB.addColorStop(0.95, 'rgba(220, 230, 250, ' + 0.15 * lum + ')');
    gB.addColorStop(1, 'rgba(200, 215, 240, 0)');
    ctx.fillStyle = gB;
    ctx.beginPath();
    ctx.arc(0, 0, blobR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
function drawGeometric(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const lum = s.luminance;
  const elapsed = performance.now() / 1000;
  // Slow, constant rotation — decoupled from state
  const rot = elapsed * 0.04;
  // Dampened scale
  const geoScale = 1.0 + (totalScale - 1.0) * 0.15;
  const radius = baseR * 0.85 * geoScale;
  // === Layer 1: Ambient glow behind everything ===
  ctx.save();
  ctx.translate(cx, cy);
  const gAmbient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 1.3);
  gAmbient.addColorStop(0, 'rgba(0, 40, 80, ' + 0.25 * lum + ')');
  gAmbient.addColorStop(0.5, 'rgba(0, 25, 50, ' + 0.1 * lum + ')');
  gAmbient.addColorStop(1, 'rgba(0, 10, 20, 0)');
  ctx.fillStyle = gAmbient;
  ctx.beginPath();
  ctx.arc(0, 0, radius * 1.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === Layer 2: Concentric orbit rings — very subtle ===
  const ringRadii = [0.45, 0.7, 0.95];
  for (let r = 0; r < ringRadii.length; r++) {
    const rr = radius * ringRadii[r];
    const alpha = (0.06 + r * 0.02) * lum;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.strokeStyle = 'rgba(0, 70, 140, ' + alpha + ')';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([4, 8]);
    ctx.beginPath();
    ctx.arc(0, 0, rr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }
  // === Layer 3: Nodes on orbits ===
  // Define node positions: [ring index, angle offset, isOrange]
  const nodes: [number, number, boolean][] = [[0, 0, false], [0, Math.PI * 0.7, true], [0, Math.PI * 1.4, false], [1, 0.3, false], [1, Math.PI * 0.5, false], [1, Math.PI, true], [1, Math.PI * 1.3, false], [1, Math.PI * 1.8, false], [2, 0.1, true], [2, Math.PI * 0.4, false], [2, Math.PI * 0.8, false], [2, Math.PI * 1.1, false], [2, Math.PI * 1.5, true], [2, Math.PI * 1.9, false]];
  // Calculate node positions
  const nodePositions: {
    x: number;
    y: number;
    isOrange: boolean;
  }[] = [];
  for (const [ringIdx, angleOff, isOrange] of nodes) {
    const rr = radius * ringRadii[ringIdx];
    // Each ring rotates at slightly different speed for parallax
    const speed = 1.0 + ringIdx * 0.3;
    const angle = angleOff + rot * speed;
    nodePositions.push({
      x: cx + Math.cos(angle) * rr,
      y: cy + Math.sin(angle) * rr,
      isOrange
    });
  }
  // === Layer 4: Connection lines between nearby nodes ===
  const connectionDist = radius * 0.55;
  ctx.save();
  for (let i = 0; i < nodePositions.length; i++) {
    for (let j = i + 1; j < nodePositions.length; j++) {
      const a = nodePositions[i];
      const b = nodePositions[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < connectionDist) {
        const proximity = 1 - dist / connectionDist;
        const lineAlpha = proximity * 0.15 * lum;
        if (a.isOrange || b.isOrange) {
          ctx.strokeStyle = 'rgba(200, 120, 50, ' + lineAlpha * s.orangeGlow + ')';
        } else {
          ctx.strokeStyle = 'rgba(60, 140, 220, ' + lineAlpha + ')';
        }
        ctx.lineWidth = 0.8 + proximity * 0.5;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
  // === Layer 5: Draw nodes ===
  for (const node of nodePositions) {
    ctx.save();
    ctx.translate(node.x, node.y);
    if (node.isOrange) {
      // Orange accent node — warm glow
      const oGlowR = 12 + s.orangeGlow * 6;
      const oAlpha = (0.4 + s.orangeGlow * 0.4) * lum;
      const gO = ctx.createRadialGradient(0, 0, 0, 0, 0, oGlowR);
      gO.addColorStop(0, 'rgba(232, 119, 34, ' + oAlpha + ')');
      gO.addColorStop(0.4, 'rgba(220, 100, 30, ' + oAlpha * 0.3 + ')');
      gO.addColorStop(1, 'rgba(180, 80, 20, 0)');
      ctx.fillStyle = gO;
      ctx.beginPath();
      ctx.arc(0, 0, oGlowR, 0, Math.PI * 2);
      ctx.fill();
      // Bright core
      ctx.fillStyle = 'rgba(255, 180, 100, ' + 0.9 * lum + ')';
      ctx.beginPath();
      ctx.arc(0, 0, 2.5, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Blue node — cool, subtle
      const bGlowR = 8 + s.coreFocus * 4;
      const bAlpha = (0.2 + s.coreFocus * 0.15) * lum;
      const gN = ctx.createRadialGradient(0, 0, 0, 0, 0, bGlowR);
      gN.addColorStop(0, 'rgba(80, 170, 255, ' + bAlpha + ')');
      gN.addColorStop(1, 'rgba(40, 100, 180, 0)');
      ctx.fillStyle = gN;
      ctx.beginPath();
      ctx.arc(0, 0, bGlowR, 0, Math.PI * 2);
      ctx.fill();
      // Core dot
      ctx.fillStyle = 'rgba(150, 210, 255, ' + 0.6 * lum + ')';
      ctx.beginPath();
      ctx.arc(0, 0, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  // === Layer 6: Central B lettermark ===
  ctx.save();
  const bSize = baseR * 0.45;
  ctx.font = '700 ' + bSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Glow behind B
  ctx.globalAlpha = 0.08 * lum;
  ctx.fillStyle = 'rgba(80, 160, 240, 1)';
  ctx.shadowColor = 'rgba(40, 100, 200, 0.5)';
  ctx.shadowBlur = baseR * 0.25;
  ctx.fillText('B', cx, cy);
  // Main B — Brookfield navy, subtle
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 0.2 * lum;
  ctx.fillStyle = 'rgba(120, 180, 240, 1)';
  ctx.fillText('B', cx, cy);
  ctx.restore();
}
function drawRing(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const lum = s.luminance;
  // Ring radius uses a VERY gentle scale — almost no size change between states
  // This prevents the jarring resize effect
  const ringScale = 1.0 + (totalScale - 1.0) * 0.15; // Dampen scale to ~15% of original
  const ringR = baseR * 0.85 * ringScale;
  // Use raw elapsed time for the dot orbit — decoupled from state speed
  // This prevents the dot from jumping/accelerating on state change
  const elapsed = performance.now() / 1000 * 0.4; // Constant gentle orbit speed
  // === Outer ambient glow — responds to state via luminance ===
  ctx.save();
  ctx.translate(cx, cy);
  const glowSize = ringR * (1.3 + s.coreFocus * 0.15); // Very subtle size response
  const gGlow = ctx.createRadialGradient(0, 0, ringR * 0.6, 0, 0, glowSize);
  gGlow.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gGlow.addColorStop(0.4, 'rgba(30, 100, 200, ' + 0.08 * lum + ')');
  gGlow.addColorStop(0.7, 'rgba(40, 120, 220, ' + 0.12 * lum + ')');
  gGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gGlow;
  ctx.beginPath();
  ctx.arc(0, 0, glowSize, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === Main ring — width changes very subtly ===
  const ringWidth = 2.0 + s.coreFocus * 1.0; // Much gentler: 2.0 → 3.0 range
  ctx.save();
  ctx.translate(cx, cy);
  // Ring brightness pulses gently instead of size changing
  const ringPulse = Math.sin(performance.now() * 0.001) * 0.08;
  const ringAlpha = Math.min(1, (0.65 + ringPulse + s.coreFocus * 0.2) * lum);
  ctx.strokeStyle = 'rgba(200, 220, 255, ' + ringAlpha + ')';
  ctx.lineWidth = ringWidth;
  ctx.beginPath();
  ctx.arc(0, 0, ringR, 0, Math.PI * 2);
  ctx.stroke();
  // === Inner ring — very subtle, barely visible ===
  const innerAlpha = (0.1 + s.coreFocus * 0.08) * lum;
  ctx.strokeStyle = 'rgba(100, 160, 230, ' + innerAlpha + ')';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(0, 0, ringR * 0.65, 0, Math.PI * 2);
  ctx.stroke();
  // === Speaking/emphasis: faint expanding pulse ring ===
  if (s.breathe > 0.01) {
    const pulsePhase = performance.now() * 0.0015 % 1;
    const pulseR = ringR * (1.0 + pulsePhase * 0.4);
    const pulseAlpha = (1 - pulsePhase) * 0.15 * lum * (s.breathe / 0.035);
    if (pulseAlpha > 0.01) {
      ctx.strokeStyle = 'rgba(150, 200, 255, ' + pulseAlpha + ')';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(0, 0, pulseR, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.restore();
  // === Orbiting dot — smooth, constant speed ===
  const dotAngle = elapsed * Math.PI * 2 * 0.15; // ~one orbit per 6.7 seconds, always smooth
  const dx = cx + Math.cos(dotAngle) * ringR;
  const dy = cy + Math.sin(dotAngle) * ringR;
  // Dot outer glow — size responds gently to state
  const dotGlowR = 16 + s.orangeGlow * 8; // 16-24 range
  const dotAlpha = 0.5 + s.orangeGlow * 0.4; // 0.5-0.9 range
  ctx.save();
  ctx.translate(dx, dy);
  const gDot = ctx.createRadialGradient(0, 0, 0, 0, 0, dotGlowR);
  gDot.addColorStop(0, 'rgba(232, 119, 34, ' + dotAlpha + ')');
  gDot.addColorStop(0.35, 'rgba(245, 145, 61, ' + dotAlpha * 0.4 + ')');
  gDot.addColorStop(1, 'rgba(200, 80, 10, 0)');
  ctx.fillStyle = gDot;
  ctx.beginPath();
  ctx.arc(0, 0, dotGlowR, 0, Math.PI * 2);
  ctx.fill();
  // Dot bright core
  const coreAlpha = 0.7 + s.orangeGlow * 0.25;
  ctx.fillStyle = 'rgba(255, 210, 160, ' + coreAlpha + ')';
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === Center point — gentle glow ===
  ctx.save();
  ctx.translate(cx, cy);
  const centerGlowR = baseR * 0.12;
  const centerAlpha = 0.12 + s.coreFocus * 0.25;
  const gCP = ctx.createRadialGradient(0, 0, 0, 0, 0, centerGlowR);
  gCP.addColorStop(0, 'rgba(200, 230, 255, ' + centerAlpha * lum + ')');
  gCP.addColorStop(0.5, 'rgba(100, 170, 240, ' + centerAlpha * 0.3 * lum + ')');
  gCP.addColorStop(1, 'rgba(0, 50, 100, 0)');
  ctx.fillStyle = gCP;
  ctx.beginPath();
  ctx.arc(0, 0, centerGlowR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
// --- Gradient Variant Configs ---
interface GradientVariantConfig {
  // Gold blob defaults
  goldBaseSpreadX: number; // how far left from center (multiplier of baseR)
  goldBaseSpreadY: number; // how far down from center
  goldBaseSize: number; // base radius multiplier
  goldBaseAlpha: number; // base opacity
  goldSizeOnSpeak: number; // additional size when speaking (driven by orangeGlow)
  goldAlphaOnSpeak: number; // additional alpha when speaking
  goldSizeOnListen: number; // additional size when listening (driven by coreFocus)
  goldAlphaOnListen: number; // additional alpha when listening
  // Blue blob defaults
  blueBaseSpreadX: number;
  blueBaseSpreadY: number;
  blueBaseSize: number;
  blueBaseAlpha: number;
  blueSizeOnSpeak: number;
  blueAlphaOnSpeak: number;
  blueSizeOnListen: number;
  blueAlphaOnListen: number;
  // Movement
  driftMultiplier: number; // how much blobs drift
  spreadDriftScale: number; // how much spread changes with state
}
const GRADIENT_VARIANTS: Record<string, GradientVariantConfig> = {
  // 1. Bright Gradient — match reference exactly
  // Gold: large, left-center, slightly below. Blue: upper-right, smaller, clearly separated
  gradient1: {
    goldBaseSpreadX: 0.4,
    goldBaseSpreadY: -0.12,
    goldBaseSize: 1.25,
    goldBaseAlpha: 0.85,
    goldSizeOnSpeak: 0.3,
    goldAlphaOnSpeak: 0.12,
    goldSizeOnListen: -0.2,
    goldAlphaOnListen: -0.15,
    blueBaseSpreadX: 0.5,
    blueBaseSpreadY: 0.3,
    blueBaseSize: 0.9,
    blueBaseAlpha: 0.78,
    blueSizeOnSpeak: 0.2,
    blueAlphaOnSpeak: 0.1,
    blueSizeOnListen: 0.25,
    blueAlphaOnListen: 0.15,
    driftMultiplier: 1.0,
    spreadDriftScale: 0.15
  },
  // 2. Bold Bloom — larger orbs, bolder presence, well-separated
  gradient2: {
    goldBaseSpreadX: 0.45,
    goldBaseSpreadY: -0.12,
    goldBaseSize: 1.35,
    goldBaseAlpha: 0.92,
    goldSizeOnSpeak: 0.3,
    goldAlphaOnSpeak: 0.06,
    goldSizeOnListen: -0.2,
    goldAlphaOnListen: -0.08,
    blueBaseSpreadX: 0.55,
    blueBaseSpreadY: 0.3,
    blueBaseSize: 1.05,
    blueBaseAlpha: 0.88,
    blueSizeOnSpeak: 0.25,
    blueAlphaOnSpeak: 0.08,
    blueSizeOnListen: 0.15,
    blueAlphaOnListen: 0.08,
    driftMultiplier: 1.2,
    spreadDriftScale: 0.12
  },
  // 3. Golden Lead — gold dominant and central, blue far off to side
  gradient3: {
    goldBaseSpreadX: 0.25,
    goldBaseSpreadY: -0.05,
    goldBaseSize: 1.4,
    goldBaseAlpha: 0.92,
    goldSizeOnSpeak: 0.1,
    goldAlphaOnSpeak: 0.05,
    goldSizeOnListen: -0.15,
    goldAlphaOnListen: -0.08,
    blueBaseSpreadX: 0.6,
    blueBaseSpreadY: 0.35,
    blueBaseSize: 0.6,
    blueBaseAlpha: 0.35,
    blueSizeOnSpeak: 0.65,
    blueAlphaOnSpeak: 0.5,
    blueSizeOnListen: 0.1,
    blueAlphaOnListen: 0.1,
    driftMultiplier: 0.8,
    spreadDriftScale: 0.2
  },
  // 4. Navy Lead — blue dominant and central, gold far off to side
  gradient4: {
    goldBaseSpreadX: 0.6,
    goldBaseSpreadY: -0.2,
    goldBaseSize: 0.6,
    goldBaseAlpha: 0.35,
    goldSizeOnSpeak: 0.65,
    goldAlphaOnSpeak: 0.5,
    goldSizeOnListen: 0.1,
    goldAlphaOnListen: 0.08,
    blueBaseSpreadX: 0.25,
    blueBaseSpreadY: 0.05,
    blueBaseSize: 1.35,
    blueBaseAlpha: 0.92,
    blueSizeOnSpeak: 0.1,
    blueAlphaOnSpeak: 0.05,
    blueSizeOnListen: 0.15,
    blueAlphaOnListen: 0.06,
    driftMultiplier: 0.8,
    spreadDriftScale: 0.2
  },
  // 5. Compact Duo — smaller blobs, more white space, dramatic expansion
  gradient5: {
    goldBaseSpreadX: 0.38,
    goldBaseSpreadY: -0.1,
    goldBaseSize: 0.85,
    goldBaseAlpha: 0.9,
    goldSizeOnSpeak: 0.45,
    goldAlphaOnSpeak: 0.06,
    goldSizeOnListen: -0.15,
    goldAlphaOnListen: -0.08,
    blueBaseSpreadX: 0.48,
    blueBaseSpreadY: 0.28,
    blueBaseSize: 0.7,
    blueBaseAlpha: 0.88,
    blueSizeOnSpeak: 0.35,
    blueAlphaOnSpeak: 0.08,
    blueSizeOnListen: -0.1,
    blueAlphaOnListen: -0.06,
    driftMultiplier: 0.7,
    spreadDriftScale: 0.25
  }
};
function drawGradientVariant(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number, variantKey: string, pairRotation: number) {
  const elapsed = performance.now() / 1000;
  const W = cx * 2;
  const H = cy * 2;
  const v = GRADIENT_VARIANTS[variantKey] || GRADIENT_VARIANTS.gradient1;
  // Clean white background
  ctx.save();
  ctx.fillStyle = '#F5F5F5';
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
  // === State signals (change smoothly via lerp) ===
  const warmth = Math.max(0, Math.min(1, s.orangeGlow / 0.9));
  const focus = s.coreFocus;
  const energy = Math.max(0, Math.min(1, (s.drift - 0.4) / 1.2));
  // === GLOBAL SCALE — amplified from s.scale for dramatic listening shrink ===
  const globalScale = 1.0 + (s.scale - 1.0) * 2.5;
  // === Sizes ===
  const goldSize = baseR * (v.goldBaseSize + warmth * v.goldSizeOnSpeak + focus * v.goldSizeOnListen) * globalScale;
  const blueSize = baseR * (v.blueBaseSize + warmth * v.blueSizeOnSpeak + focus * v.blueSizeOnListen) * globalScale;
  // === Alphas ===
  const goldAlpha = Math.max(0.08, Math.min(1.0, v.goldBaseAlpha + warmth * v.goldAlphaOnSpeak + focus * v.goldAlphaOnListen));
  const blueAlpha = Math.max(0.08, Math.min(1.0, v.blueBaseAlpha + warmth * v.blueAlphaOnSpeak + focus * v.blueAlphaOnListen));
  // === Spread ===
  const spreadMod = (0.55 + energy * 0.65) * globalScale;
  // pairRotation is passed in from the render loop (accumulated, burst-then-settle)
  // === Very gentle ambient drift ===
  const driftAmt = baseR * 0.025 * v.driftMultiplier;
  const driftFreq = 0.015 * v.driftMultiplier;
  const goldDriftX = snoise(elapsed * driftFreq, 0) * driftAmt;
  const goldDriftY = snoise(elapsed * driftFreq, 5) * driftAmt;
  const blueDriftX = snoise(elapsed * driftFreq + 50, 0) * driftAmt;
  const blueDriftY = snoise(elapsed * driftFreq + 50, 5) * driftAmt;
  // === GOLD BLOB POSITION ===
  const goldOffX = -v.goldBaseSpreadX * spreadMod;
  const goldOffY = v.goldBaseSpreadY * spreadMod;
  const goldRotX = goldOffX * Math.cos(pairRotation) - goldOffY * Math.sin(pairRotation);
  const goldRotY = goldOffX * Math.sin(pairRotation) + goldOffY * Math.cos(pairRotation);
  const goldX = cx + baseR * goldRotX + goldDriftX;
  const goldY = cy + baseR * goldRotY + goldDriftY;
  // === DRAW GOLD ===
  ctx.save();
  ctx.translate(goldX, goldY);
  const gGold = ctx.createRadialGradient(0, 0, 0, 0, 0, goldSize);
  gGold.addColorStop(0, 'rgba(240, 195, 60, ' + goldAlpha + ')');
  gGold.addColorStop(0.2, 'rgba(238, 188, 55, ' + goldAlpha * 0.88 + ')');
  gGold.addColorStop(0.4, 'rgba(235, 180, 50, ' + goldAlpha * 0.58 + ')');
  gGold.addColorStop(0.6, 'rgba(242, 200, 85, ' + goldAlpha * 0.25 + ')');
  gGold.addColorStop(0.8, 'rgba(248, 220, 130, ' + goldAlpha * 0.06 + ')');
  gGold.addColorStop(1, 'rgba(252, 238, 175, 0)');
  ctx.fillStyle = gGold;
  ctx.beginPath();
  ctx.arc(0, 0, goldSize, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === BLUE BLOB POSITION ===
  const blueOffX = v.blueBaseSpreadX * spreadMod;
  const blueOffY = -v.blueBaseSpreadY * spreadMod;
  const blueRotX = blueOffX * Math.cos(pairRotation) - blueOffY * Math.sin(pairRotation);
  const blueRotY = blueOffX * Math.sin(pairRotation) + blueOffY * Math.cos(pairRotation);
  const blueX = cx + baseR * blueRotX + blueDriftX;
  const blueY = cy + baseR * blueRotY + blueDriftY;
  // === DRAW BLUE ===
  ctx.save();
  ctx.translate(blueX, blueY);
  const gBlue = ctx.createRadialGradient(0, 0, 0, 0, 0, blueSize);
  gBlue.addColorStop(0, 'rgba(50, 100, 220, ' + blueAlpha + ')');
  gBlue.addColorStop(0.2, 'rgba(60, 112, 225, ' + blueAlpha * 0.85 + ')');
  gBlue.addColorStop(0.4, 'rgba(75, 128, 232, ' + blueAlpha * 0.48 + ')');
  gBlue.addColorStop(0.6, 'rgba(110, 158, 240, ' + blueAlpha * 0.18 + ')');
  gBlue.addColorStop(0.8, 'rgba(160, 195, 248, ' + blueAlpha * 0.04 + ')');
  gBlue.addColorStop(1, 'rgba(200, 220, 252, 0)');
  ctx.fillStyle = gBlue;
  ctx.beginPath();
  ctx.arc(0, 0, blueSize, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === WHITE B LETTERMARK ===
  ctx.save();
  const bSize = baseR * 0.85 * globalScale;
  ctx.font = '800 ' + bSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const bAlpha = 0.65 + energy * 0.18 + warmth * 0.1;
  ctx.shadowColor = 'rgba(255, 255, 255, ' + bAlpha * 0.5 + ')';
  ctx.shadowBlur = baseR * 0.1;
  ctx.fillStyle = 'rgba(255, 255, 255, ' + bAlpha + ')';
  ctx.fillText('B', cx, cy);
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255, 255, 255, ' + bAlpha * 0.9 + ')';
  ctx.fillText('B', cx, cy);
  ctx.restore();
}
function drawGradient(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const elapsed = performance.now() / 1000;
  const W = cx * 2;
  const H = cy * 2;
  // Clean white background
  ctx.save();
  ctx.fillStyle = '#F5F5F5';
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
  // Movement speed — listening=slow, speaking=energetic
  const driftSpeed = 0.02 + s.drift * 0.04; // 0.036 (listening) → 0.084 (speaking)
  const orbitSpeed = 0.02 + s.drift * 0.03; // gentle orbit that speeds up when speaking
  // Blob spread — how far apart the blobs sit
  // listening: close together (compact), speaking: spread apart (expansive)
  const spread = 0.1 + s.drift * 0.12; // 0.148 (listening) → 0.292 (speaking)
  // Orbit — blobs slowly circle when speaking, nearly still when listening
  const orbitAngle = elapsed * orbitSpeed;
  const orbitX = Math.cos(orbitAngle) * baseR * spread * 0.3;
  const orbitY = Math.sin(orbitAngle) * baseR * spread * 0.2;
  // === GOLD BLOB ===
  const goldBaseX = cx - baseR * spread;
  const goldBaseY = cy + baseR * spread * 0.3;
  const goldDriftX = snoise(elapsed * driftSpeed, 0) * baseR * 0.06 * s.drift;
  const goldDriftY = snoise(elapsed * driftSpeed, 5) * baseR * 0.05 * s.drift;
  const goldX = goldBaseX + goldDriftX + orbitX;
  const goldY = goldBaseY + goldDriftY + orbitY;
  // Gold size — bigger when speaking/emphasis (warm energy)
  const goldR = baseR * (0.65 + s.orangeGlow * 0.4); // 0.71 (listening) → 0.87 (speaking) → 1.01 (emphasis)
  // Gold opacity — muted when listening, vivid when speaking
  const goldAlpha = 0.5 + s.orangeGlow * 0.5; // 0.575 (listening) → 0.775 (speaking) → 0.95 (emphasis)
  // Gold edge tightness — softer when listening, tighter core when speaking
  const goldTightness = 0.3 + s.drift * 0.12; // controls mid-stop position
  ctx.save();
  ctx.translate(goldX, goldY);
  const goldSquash = 1.0 + snoise(elapsed * 0.05, 10) * 0.06 * s.drift;
  ctx.scale(goldSquash, 1.02 / goldSquash);
  ctx.rotate(snoise(elapsed * 0.03, 15) * 0.08 * s.drift);
  const gGold = ctx.createRadialGradient(0, 0, 0, 0, 0, goldR);
  gGold.addColorStop(0, 'rgba(235, 185, 50, ' + goldAlpha + ')');
  gGold.addColorStop(0.2, 'rgba(230, 175, 45, ' + goldAlpha * 0.85 + ')');
  gGold.addColorStop(goldTightness + 0.1, 'rgba(225, 165, 40, ' + goldAlpha * 0.55 + ')');
  gGold.addColorStop(0.6, 'rgba(235, 190, 70, ' + goldAlpha * 0.22 + ')');
  gGold.addColorStop(0.8, 'rgba(245, 215, 120, ' + goldAlpha * 0.05 + ')');
  gGold.addColorStop(1, 'rgba(250, 230, 160, 0)');
  ctx.fillStyle = gGold;
  ctx.beginPath();
  ctx.arc(0, 0, goldR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === BLUE BLOB ===
  const blueBaseX = cx + baseR * spread;
  const blueBaseY = cy - baseR * spread * 0.4;
  const blueDriftX = snoise(elapsed * driftSpeed + 30, 0) * baseR * 0.06 * s.drift;
  const blueDriftY = snoise(elapsed * driftSpeed + 30, 5) * baseR * 0.05 * s.drift;
  const blueX = blueBaseX + blueDriftX - orbitX;
  const blueY = blueBaseY + blueDriftY - orbitY;
  // Blue size — bigger when listening/emphasis (cool focus)
  const blueR = baseR * (0.6 + s.coreFocus * 0.3 + s.drift * 0.1);
  // 0.6 + 0.135 + 0.04 = 0.775 (listening)
  // 0.6 + 0.045 + 0.16 = 0.805 (speaking)
  // 0.6 + 0.225 + 0.08 = 0.905 (emphasis)
  // Blue opacity — stronger when listening (cool/calm), moderate when speaking
  const blueAlpha = 0.5 + s.coreFocus * 0.35 + s.luminance * 0.1;
  // listening: 0.5 + 0.1575 + 0.075 = 0.73
  // speaking: 0.5 + 0.0525 + 0.13 = 0.68
  // emphasis: 0.5 + 0.2625 + 0.15 = 0.91
  const blueTightness = 0.3 + s.drift * 0.1;
  ctx.save();
  ctx.translate(blueX, blueY);
  const blueSquash = 1.0 + snoise(elapsed * 0.045, 25) * 0.06 * s.drift;
  ctx.scale(1.02 / blueSquash, blueSquash);
  ctx.rotate(snoise(elapsed * 0.025, 30) * 0.08 * s.drift);
  const gBlue = ctx.createRadialGradient(0, 0, 0, 0, 0, blueR);
  gBlue.addColorStop(0, 'rgba(45, 95, 210, ' + blueAlpha + ')');
  gBlue.addColorStop(0.2, 'rgba(55, 105, 215, ' + blueAlpha * 0.82 + ')');
  gBlue.addColorStop(blueTightness + 0.1, 'rgba(70, 120, 220, ' + blueAlpha * 0.45 + ')');
  gBlue.addColorStop(0.6, 'rgba(100, 150, 230, ' + blueAlpha * 0.15 + ')');
  gBlue.addColorStop(0.8, 'rgba(150, 185, 240, ' + blueAlpha * 0.03 + ')');
  gBlue.addColorStop(1, 'rgba(190, 210, 245, 0)');
  ctx.fillStyle = gBlue;
  ctx.beginPath();
  ctx.arc(0, 0, blueR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === SPEAKING PULSE — subtle expanding ring when speaking ===
  if (s.breathe > 0.01) {
    const pulsePhase = elapsed * 1.5 % 1;
    const pulseR = baseR * (0.3 + pulsePhase * 0.5);
    const pulseAlpha = (1 - pulsePhase) * 0.06 * (s.breathe / 0.035);
    if (pulseAlpha > 0.005) {
      ctx.save();
      ctx.strokeStyle = 'rgba(200, 180, 120, ' + pulseAlpha + ')';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }
  // === WHITE B LETTERMARK ===
  ctx.save();
  const bSize = baseR * 0.85;
  ctx.font = '800 ' + bSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // B opacity — softer when listening, crisper when speaking/emphasis
  const bAlpha = 0.65 + s.drift * 0.15 + Math.max(0, s.luminance - 1.0) * 0.15;
  // listening: 0.65 + 0.06 + 0 = 0.71
  // idle: 0.65 + 0.15 + 0 = 0.80
  // speaking: 0.65 + 0.24 + 0.045 = 0.935
  // emphasis: 0.65 + 0.12 + 0.075 = 0.845
  // Soft glow
  ctx.shadowColor = 'rgba(255, 255, 255, ' + bAlpha * 0.6 + ')';
  ctx.shadowBlur = baseR * (0.15 - s.drift * 0.04); // more glow when calm, less when active
  ctx.fillStyle = 'rgba(255, 255, 255, ' + bAlpha + ')';
  ctx.fillText('B', cx, cy);
  // Crisp pass
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255, 255, 255, ' + bAlpha * 0.9 + ')';
  ctx.fillText('B', cx, cy);
  ctx.restore();
}
function drawBurst(ctx: CanvasRenderingContext2D, s: StateConfig, t: number, cx: number, cy: number, baseR: number, totalScale: number) {
  const elapsed = performance.now() / 1000;
  const W = cx * 2;
  const H = cy * 2;
  // Fill light background
  ctx.save();
  ctx.fillStyle = '#EEF1F5';
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
  const rot = elapsed * 0.03;
  // === Radial bars — softer, blurred ===
  const barCount = 64;
  const innerR = baseR * 0.48;
  const gapR = baseR * 0.05;
  const baseBarLen = baseR * (0.12 + s.drift * 0.16);
  const barVariation = s.drift * 0.6;
  const barWidth = 2.5 + s.drift * 1.0;
  // Less blur when active — bars get slightly crisper
  const blurAmount = 8 - s.drift * 2.0; // idle=6, listening=7.2, speaking=4.8
  // Saturation ramps up more for active states
  // idle drift=1.0 → 0.35, listening drift=0.4 → 0.26, speaking drift=1.6 → 0.44, emphasis drift=0.8 → 0.32
  // But we also want luminance to boost it: idle=1.0, listening=0.75, speaking=1.3, emphasis=1.5
  const saturation = 0.15 + s.drift * 0.18 + Math.max(0, s.luminance - 1.0) * 0.3;
  for (let i = 0; i < barCount; i++) {
    const angle = i / barCount * Math.PI * 2 + rot;
    const noiseSeed = i * 0.8;
    const noiseVal = noise(noiseSeed + elapsed * (0.2 + s.drift * 0.3), elapsed * 0.15);
    const barLen = baseBarLen * (0.5 + noiseVal * barVariation + (1 - barVariation) * 0.5);
    const startR = innerR + gapR;
    const endR = startR + barLen;
    const x1 = cx + Math.cos(angle) * startR;
    const y1 = cy + Math.sin(angle) * startR;
    const x2 = cx + Math.cos(angle) * endR;
    const y2 = cy + Math.sin(angle) * endR;
    const colorPhase = (i + Math.floor(elapsed * 1.5)) % 12;
    let r: number, g: number, b: number;
    if (colorPhase < 5) {
      // Soft blue — gets richer when active
      r = 100 + 50 * saturation;
      g = 140 + 60 * saturation;
      b = 200 + 45 * saturation;
    } else if (colorPhase < 9) {
      // Soft teal
      r = 80 + 40 * saturation;
      g = 180 + 40 * saturation;
      b = 200 + 35 * saturation;
    } else {
      // Soft gold
      r = 220 + 25 * saturation;
      g = 185 + 25 * saturation;
      b = 100 + 15 * saturation;
    }
    // Alpha: idle stays soft, active states get a noticeable bump
    // luminance: idle=1.0, listening=0.75, speaking=1.3, emphasis=1.5
    const lumBoost = Math.max(0, s.luminance - 0.9) * 0.25; // 0 for listening, 0.025 idle, 0.1 speaking, 0.15 emphasis
    const alpha = (0.15 + saturation * 0.4 + lumBoost) * (0.4 + noiseVal * 0.6);
    const colorStr = 'rgba(' + Math.round(r) + ', ' + Math.round(g) + ', ' + Math.round(b) + ', ';
    ctx.save();
    ctx.shadowColor = colorStr + alpha * 0.7 + ')';
    ctx.shadowBlur = blurAmount;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = colorStr + alpha + ')';
    ctx.lineWidth = barWidth;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  }
  // === Central circle ===
  ctx.save();
  ctx.translate(cx, cy);
  const circleR = innerR;
  const ringWidth = 2 + s.coreFocus * 1.5;
  ctx.strokeStyle = 'rgba(190, 210, 230, ' + (0.3 + s.luminance * 0.15) + ')';
  ctx.lineWidth = ringWidth;
  ctx.beginPath();
  ctx.arc(0, 0, circleR, 0, Math.PI * 2);
  ctx.stroke();
  const gCircle = ctx.createRadialGradient(0, 0, 0, 0, 0, circleR);
  gCircle.addColorStop(0, 'rgba(242, 246, 252, 0.97)');
  gCircle.addColorStop(0.6, 'rgba(228, 236, 248, 0.93)');
  gCircle.addColorStop(1, 'rgba(210, 224, 242, 0.88)');
  ctx.fillStyle = gCircle;
  ctx.beginPath();
  ctx.arc(0, 0, circleR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // === B lettermark ===
  ctx.save();
  const bSize = baseR * 0.55;
  const bBoldness = s.drift;
  const bAlpha = 0.2 + bBoldness * 0.35;
  ctx.font = '800 ' + bSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (bBoldness < 0.7) {
    ctx.strokeStyle = 'rgba(100, 120, 160, ' + bAlpha + ')';
    ctx.lineWidth = 1.5;
    ctx.strokeText('B', cx, cy);
    ctx.fillStyle = 'rgba(140, 155, 180, ' + bAlpha * 0.25 + ')';
    ctx.fillText('B', cx, cy);
  } else {
    ctx.fillStyle = 'rgba(25, 50, 90, ' + bAlpha + ')';
    ctx.fillText('B', cx, cy);
  }
  ctx.restore();
}
// --- Main Component ---
interface BrookfieldAvatarProps {
  state: AvatarState;
  visualStyle?: AvatarStyleType;
  className?: string;
}
export function BrookfieldAvatar({
  state,
  visualStyle = 'gradient1',
  className = ''
}: BrookfieldAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const sizeRef = useRef({
    w: 600,
    h: 600
  });
  const configRef = useRef<StateConfig>({
    ...STATES.idle
  });
  const startTime = useRef(performance.now());
  const rotationRef = useRef(0); // accumulated rotation angle in radians
  const speakingEntryRef = useRef<number | null>(null); // when speaking state was entered
  const prevEnergyRef = useRef(0); // to detect speaking entry
  const lastFrameRef = useRef(performance.now()); // for delta time
  // Load B image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = 'https://cdn.magicpatterns.com/uploads/c5sySafZPUH1ANHvr53iDf/brookfield-b.png';
    img.onload = () => {
      imgRef.current = img;
    };
  }, []);
  // Resize
  useEffect(() => {
    const onResize = () => {
      const c = canvasRef.current;
      if (!c) return;
      const dpr = window.devicePixelRatio || 1;
      const r = c.getBoundingClientRect();
      sizeRef.current = {
        w: r.width,
        h: r.height
      };
      c.width = r.width * dpr;
      c.height = r.height * dpr;
      const ctx = c.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Animation
  useEffect(() => {
    const render = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      const target = STATES[state];
      configRef.current = lerpConfig(configRef.current, target, 0.03);
      const s = configRef.current;
      // === Delta time for frame-rate independent rotation ===
      const now = performance.now();
      const dt = Math.min((now - lastFrameRef.current) / 1000, 0.05); // cap at 50ms
      lastFrameRef.current = now;
      // === Compute energy for rotation ===
      const energy = Math.max(0, Math.min(1, (s.drift - 0.4) / 1.2));
      // === Detect speaking entry — trigger burst ===
      if (energy > 0.75 && prevEnergyRef.current <= 0.75) {
        speakingEntryRef.current = now;
      }
      prevEnergyRef.current = energy;
      // === Compute instantaneous rotation speed ===
      let instantRotSpeed: number;
      if (energy > 0.5 && speakingEntryRef.current !== null) {
        const timeSinceEntry = (now - speakingEntryRef.current) / 1000;
        // Burst: starts at 3.2 rad/s (~2s per turn), decays to steady 0.28 rad/s over ~2s
        const burstSpeed = 3.0 * Math.exp(-timeSinceEntry / 1.6);
        const steadySpeed = 0.28;
        instantRotSpeed = (steadySpeed + burstSpeed) * energy;
      } else {
        // Idle: barely perceptible. Listening: nearly still.
        instantRotSpeed = 0.001 + energy * energy * 0.04;
      }
      // Accumulate rotation
      rotationRef.current += instantRotSpeed * dt;
      const elapsed = (performance.now() - startTime.current) / 1000;
      const t = elapsed * s.speed * 0.3;
      const W = sizeRef.current.w;
      const H = sizeRef.current.h;
      const centerX = W / 2;
      const centerY = H / 2;
      const baseR = Math.min(W, H) * 0.34;
      const breathe = Math.sin(performance.now() * s.breatheSpeed) * s.breathe;
      const totalScale = s.scale + breathe;
      const baseDriftX = snoise(t * 0.15, 0.0) * 8 * s.drift;
      const baseDriftY = snoise(t * 0.15, 5.0) * 6 * s.drift;
      const asymX = baseR * s.shiftX;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'source-over';
      // Dispatch to style-specific renderer
      switch (visualStyle) {
        case 'gradient1':
        case 'gradient2':
        case 'gradient3':
        case 'gradient4':
        case 'gradient5':
          drawGradientVariant(ctx, s, t, centerX, centerY, baseR, totalScale, visualStyle, rotationRef.current);
          break;
        case 'orb':
          drawOrb(ctx, s, t, centerX, centerY, baseR, totalScale, baseDriftX, baseDriftY, asymX);
          break;
        case 'aura':
          drawAura(ctx, s, t, centerX, centerY, baseR, totalScale);
          break;
        case 'blobs':
          drawBlobs(ctx, s, t, centerX, centerY, baseR, totalScale);
          break;
        case 'geometric':
          drawGeometric(ctx, s, t, centerX, centerY, baseR, totalScale);
          break;
        case 'ring':
          drawRing(ctx, s, t, centerX, centerY, baseR, totalScale);
          break;
        case 'gradient':
          drawGradientVariant(ctx, s, t, centerX, centerY, baseR, totalScale, 'gradient1', rotationRef.current);
          break;
        case 'burst':
          drawBurst(ctx, s, t, centerX, centerY, baseR, totalScale);
          break;
      }
      // B Lettermark overlay (orb, ring, and aura styles)
      ctx.globalCompositeOperation = 'source-over';
      if (visualStyle === 'aura') {
        // Aura style: Draw a soft, white/lavender "B" centered in the cloud
        ctx.save();
        const bSize = baseR * 0.7;
        ctx.font = '700 ' + bSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        // Soft glow behind the B
        ctx.globalAlpha = 0.12 * s.luminance;
        ctx.fillStyle = 'rgba(180, 170, 220, 1)';
        ctx.shadowColor = 'rgba(160, 140, 210, 0.6)';
        ctx.shadowBlur = baseR * 0.4;
        ctx.fillText('B', centerX, centerY);
        // Main B letter — white/lavender, soft
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 0.25 * s.luminance;
        ctx.fillStyle = 'rgba(220, 215, 240, 1)';
        ctx.fillText('B', centerX, centerY);
        ctx.restore();
      } else if (imgRef.current && (visualStyle === 'orb' || visualStyle === 'ring')) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.globalAlpha = visualStyle === 'orb' ? 0.18 : 0.1;
        const maxS = baseR * (visualStyle === 'ring' ? 1.25 : 1.0);
        const iw = imgRef.current.naturalWidth;
        const ih = imgRef.current.naturalHeight;
        const aspect = iw / ih;
        let bW: number, bH: number;
        if (aspect > 1) {
          bW = maxS;
          bH = maxS / aspect;
        } else {
          bH = maxS;
          bW = maxS * aspect;
        }
        ctx.drawImage(imgRef.current, centerX - bW / 2, centerY - bH / 2, bW, bH);
        ctx.restore();
      }
      ctx.globalCompositeOperation = 'source-over';
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state, visualStyle]);
  return <canvas ref={canvasRef} className={'w-full max-w-[700px] aspect-square ' + className} style={{
    maxHeight: '75vh',
    maxWidth: '75vh'
  }} data-id="element-16" />;
}